import { Link } from "react-router-dom";
import { ArrowRight, Scale, Globe2, FileCheck2, Landmark, Gavel, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1634638022845-1ab614a94128?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxnbG9iYWwlMjBsb2dpc3RpY3MlMjBwb3N0YWwlMjBjYXJnb3xlbnwwfHx8fDE3ODg3ODIxMjh8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Globe2, title: "Global Tender Floating", desc: "Float RFPs, NITs, EOIs, RFQs and ITBs for operative units, administrative units and training centres — visible to vendors worldwide." },
  { icon: Scale, title: "Local-Law Compliance", desc: "Every document is drafted against the procurement law of the country of execution — GFR, FAR, EU directives and more." },
  { icon: Landmark, title: "UN Rules Fallback", desc: "Where local rules are silent, drafting falls back to the UNCITRAL Model Law and the UN Procurement Manual." },
  { icon: FileCheck2, title: "AI Legal Drafting", desc: "Generate complete, legally structured tender documents in minutes — with the governing framework cited section by section." },
  { icon: Gavel, title: "Bid Evaluation & Award", desc: "Receive bids, score them on QCBS / LCS / L1 methodologies, and award contracts on a single audited trail." },
  { icon: ShieldCheck, title: "Ethics & Anti-Corruption", desc: "Built-in integrity clauses, dispute resolution and anti-corruption provisions aligned with global best practice." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-white flex items-center justify-center">
              <Globe2 className="w-5 h-5 text-[#0050A0]" aria-hidden="true" />
            </div>
            <span className="font-serif text-2xl font-bold text-white tracking-tight">makemytender<span className="text-[#009EDB]">.com</span></span>
          </div>
          <Link
            to="/dashboard"
            data-testid="landing-enter-platform-btn"
            className="bg-white text-[#0050A0] px-6 py-2.5 text-sm font-semibold hover:bg-[#009EDB] hover:text-white transition-colors duration-200"
          >
            Enter Platform
          </Link>
        </div>
      </header>

      <section className="relative min-h-[92vh] flex items-end" style={{ background: "#00294F" }}>
        <img src={HERO_IMG} alt="Global postal logistics" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,41,79,0.4) 0%, rgba(0,41,79,0.92) 85%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 pt-40 w-full">
          <p className="text-xs uppercase tracking-[0.35em] text-[#7DD3F5] mb-6 fade-up">Global Postal Procurement Platform</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] max-w-4xl fade-up" style={{ animationDelay: "0.1s" }}>
            Float global tenders.<br />
            <span className="text-[#009EDB]">Compliant everywhere.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
            The procurement backbone for postal operators — draft legally compliant RFPs, NITs and EOIs
            aligned with local procurement law, or UN rules and global best practices where none exist.
            Manage the entire lifecycle: publish, receive bids, evaluate and award.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 fade-up" style={{ animationDelay: "0.3s" }}>
            <Link
              to="/tenders/new"
              data-testid="landing-float-tender-btn"
              className="inline-flex items-center gap-2 bg-[#009EDB] text-white px-8 py-4 text-sm font-semibold hover:bg-white hover:text-[#0050A0] transition-colors duration-200"
            >
              Float a Tender <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link
              to="/tenders"
              data-testid="landing-browse-tenders-btn"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-sm font-semibold hover:bg-white hover:text-[#0050A0] transition-colors duration-200"
            >
              Browse Live Tenders
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-3 gap-12 items-end mb-16">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#0050A0] mb-4">Capabilities</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-slate-900 leading-tight">
              One platform for the full procurement lifecycle
            </h2>
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">
            From notice to award — for operative units, administrative units, training centres and headquarters across 190+ member territories.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-slate-200">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="border-b border-r border-slate-200 p-8 hover:bg-slate-50 transition-colors duration-200" data-testid={`feature-card-${i}`}>
              <f.icon className="w-7 h-7 text-[#0050A0] mb-6" aria-hidden="true" />
              <h3 className="font-serif text-xl text-slate-900 mb-3">{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-[#00294F]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="font-serif text-lg text-white">makemytender<span className="text-[#009EDB]">.com</span></span>
          <p className="text-xs text-slate-400 tracking-wide">
            Aligned with UNCITRAL Model Law on Public Procurement · UN Procurement Manual · World Bank Regulations
          </p>
        </div>
      </footer>
    </div>
  );
}
